import React, { useState, useEffect } from 'react';
import _partition from 'lodash/partition';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import useAnalytics from 'helpers/useAnalytics';
import { useSelector, useDispatch } from 'react-redux';
import { Formik, Form } from 'formik';
import { useStatus, useData, isLoading, isNormal } from 'resift';
import usePrevious from '@sift/resift/usePrevious';
import makeUpdateWidgetLayout from 'fetches/homepageWidgets/makeUpdateWidgetLayout';
import createWidgetLayout from 'fetches/homepageWidgets/createWidgetLayout';
import { setWidgets } from 'store/homepageWidgets';
import showSnackbar from 'helpers/showSnackbar';
import getHostName from 'helpers/getHostName';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { SortableContainer, SortableElement, SortableHandle, arrayMove } from 'react-sortable-hoc';
import ReorderIcon from '@material-ui/icons/DragHandle';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Collapse from '@material-ui/core/Collapse';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import Popover from '@material-ui/core/Popover';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import PageTitle from 'components/Page/PageTitle';
import PageFormToolbar from 'components/Page/newComponents/PageFormToolbar';
import SaveButton from 'components/Page/SaveButton';
import CancelButton from 'components/Page/CancelButton';
import LoadingButton from 'atoms/LoadingButton';
import LayoutReference from './LayoutReference';
import WidgetToLayoutSelector from './WidgetToLayoutSelector';
import { Widget, WidgetOnLayout } from 'pages/Homepage/typings';
import { PREVIEW_PARAM_NAME } from 'pages/Homepage/helpers/consts';

interface Props {
  widgetsOnDefaultLayout: Array<WidgetOnLayout>;
}

function LayoutTab({ widgetsOnDefaultLayout: _widgetsOnDefaultLayout }: Props) {
  const classes = useStyles();
  const analytics = useAnalytics();
  const widgetsData: Array<Widget> = useSelector(state =>
    _get(state, ['homepageWidgets', 'widgets']),
  );
  const dispatch = useDispatch();
  const updateDefaultWidgetLayout = makeUpdateWidgetLayout('default');
  const updateDefaultWidgetLayoutStatus = useStatus(updateDefaultWidgetLayout);
  const previewLayoutData = useData(createWidgetLayout);
  const previewLayoutCreateStatus = useStatus(createWidgetLayout);
  const prevPreviewLayoutData = usePrevious(previewLayoutData);
  const [widgetsOnLayout, restOfWidgets] = _partition(widgetsData, '_isInDefaultLayout');
  const [widgetOptions, setWidgetOptions] = useState(restOfWidgets);
  const widgetsOnDefaultLayout = _widgetsOnDefaultLayout.map(w => ({
    ...w,
    ...widgetsOnLayout.find(widget => widget.id === w.id),
  })) as Array<Widget>;
  const clientSubdomain = useSelector((state: any) => state.client.subdomain);
  const hostName = getHostName();

  const [
    widgetSelectorAnchorEl,
    setWidgetSelectorAnchorEl,
  ] = React.useState<HTMLButtonElement | null>(null);

  const widgetSelectorOpen = Boolean(widgetSelectorAnchorEl);
  const widgetSelectorId = widgetSelectorOpen ? 'widget-selector' : undefined;

  // @ts-ignore: incorrect typing interpretation
  const SortableHandleElem = SortableHandle(({ className, value }) => (
    <ReorderIcon className={className} data-cy={`drag-icon-${value}`} />
  ));

  const toggleOpenWidgetSelector = (event: React.MouseEvent<HTMLButtonElement>) => {
    analytics.track('Add Widget Clicked', {
      category: 'AD - Homepage',
    });

    widgetSelectorAnchorEl
      ? setWidgetSelectorAnchorEl(null)
      : setWidgetSelectorAnchorEl(event.currentTarget);
  };

  showSnackbar({
    status: updateDefaultWidgetLayoutStatus,
    message: 'Successfully updated your widget layout.',
    variant: 'success',
    errorMessage: 'An error occurred while saving your widget layout.',
  });

  useEffect(() => {
    if (isNormal(previewLayoutCreateStatus) && prevPreviewLayoutData !== previewLayoutData) {
      window.open(
        `https://${clientSubdomain}.${hostName}.com?${PREVIEW_PARAM_NAME}=${previewLayoutData.id}`,
        '_blank',
      );
    }
  }, [prevPreviewLayoutData, previewLayoutData, previewLayoutCreateStatus]);

  return (
    <Formik initialValues={{ draftWidgetsOnLayout: widgetsOnDefaultLayout }} onSubmit={() => {}}>
      {props => {
        const { values, setFieldValue, resetForm, dirty } = props;
        const { draftWidgetsOnLayout } = values;

        const handleRemoveItem = (itemValue: string) => {
          setFieldValue(
            'draftWidgetsOnLayout',
            draftWidgetsOnLayout.filter(item => item.name !== itemValue),
          );
          const item = draftWidgetsOnLayout.find(item => item.name === itemValue) as Widget;
          setWidgetOptions([...widgetOptions, item]);
        };

        // @ts-ignore: incorrect typing interpretation
        const SortableItem = SortableElement(({ value }) => {
          return (
            <Collapse
              in={draftWidgetsOnLayout.map(w => w.name).includes(value)}
              timeout={250}
              data-cy="sortable-item"
            >
              <div className={classes.item}>
                {/* @ts-ignore: incorrect typing interpretation */}
                <SortableHandleElem className={classes.dragHandleIcon} value={value} />
                <div className={classes.fieldNameContainer}>
                  <Typography
                    variant="body1"
                    className={classes.fieldName}
                    data-cy={`sortable-item-text-${value}`}
                  >
                    {value}
                  </Typography>
                </div>
                <div className={classes.rightIconsContainer}>
                  <IconButton
                    aria-label="remove"
                    onClick={() => handleRemoveItem(value)}
                    className={classes.removeCircleIconButton}
                  >
                    <RemoveCircleOutlineIcon className={classes.removeCircleIcon} />
                  </IconButton>
                </div>
              </div>
            </Collapse>
          );
        });

        // @ts-ignore: incorrect typing interpretation
        const SortableList = SortableContainer(({ items }) => {
          return (
            <div className={classes.sortableList} data-cy="sortable-list">
              {items.map((value: any, index: number) => (
                // @ts-ignore: incorrect typing interpretation
                <SortableItem key={`item-${value}-${index}`} index={index} value={value.name} />
              ))}
            </div>
          );
        });

        const handleSortEnd = ({ oldIndex, newIndex }: any) => {
          const updatedWidgetsLayout = arrayMove(draftWidgetsOnLayout, oldIndex, newIndex);
          setFieldValue('draftWidgetsOnLayout', updatedWidgetsLayout);
        };

        const handleAddWidgetToLayout = (item: Widget) => {
          setFieldValue('draftWidgetsOnLayout', [...draftWidgetsOnLayout, item]);
          setWidgetOptions(widgetOptions.filter(option => option.name !== item.name));
          setWidgetSelectorAnchorEl(null);
        };

        const widgetLayoutRequest = values.draftWidgetsOnLayout.map(l => ({ id: l.id }));

        const handleSave = () => {
          dispatch(
            updateDefaultWidgetLayout({
              widgets: widgetLayoutRequest,
            }),
          );

          resetForm({ values });
          dispatch(
            setWidgets([
              ...values.draftWidgetsOnLayout.map(w => ({ ...w, _isInDefaultLayout: true })),
              ...widgetOptions.map(w => ({ ...w, _isInDefaultLayout: false })),
            ]),
          );
        };

        const handleCreateWidgetPreview = () => {
          analytics.track('Preview Layout Clicked', {
            category: 'AD - Homepage',
          });

          dispatch(createWidgetLayout({ widgets: widgetLayoutRequest }));
        };

        return (
          <Form className={classes.form}>
            <PageTitle
              subtitle={
                <>
                  <Typography variant="subtitle2">
                    Use the list below to select the widgets that you would like to have visible on
                    your homepage. Use the Preview Layout button to preview your homepage before
                    releasing it to your end users.
                  </Typography>

                  <Typography variant="subtitle2">
                    If you choose to not add any widgets to your layout, the Sift homepage will
                    display a larger search bar without the widget area displayed.
                  </Typography>
                </>
              }
            />
            <div className={classes.cards}>
              <Card className={classes.card}>
                <CardHeader
                  title={<Typography variant="h5">Layout Reference</Typography>}
                  action={
                    <LoadingButton
                      onClick={handleCreateWidgetPreview}
                      color="secondary"
                      className={classes.previewButton}
                      loading={isLoading(previewLayoutCreateStatus)}
                      label="Preview Layout"
                      variant="text"
                    />
                  }
                />
                <CardContent className={classes.cardContent}>
                  <LayoutReference numberOfWidgets={draftWidgetsOnLayout.length} />
                </CardContent>
              </Card>
              <Card className={classes.card}>
                <CardHeader
                  title={<Typography variant="h5">Widget List (6 Max)</Typography>}
                  action={
                    <div className={classes.widgetSelector}>
                      <Tooltip arrow title="Only six widgets may be added" placement="top">
                        <div>
                          <Button
                            onClick={toggleOpenWidgetSelector}
                            color="secondary"
                            aria-describedby={widgetSelectorId}
                            disabled={draftWidgetsOnLayout.length >= 6}
                          >
                            Add Widget
                          </Button>
                        </div>
                      </Tooltip>

                      <Popover
                        id={widgetSelectorId}
                        open={widgetSelectorOpen}
                        anchorEl={widgetSelectorAnchorEl}
                        onClose={toggleOpenWidgetSelector}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'center',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'center',
                        }}
                      >
                        <WidgetToLayoutSelector
                          availableWidgets={widgetOptions}
                          onSelectWidget={handleAddWidgetToLayout}
                        />
                      </Popover>
                    </div>
                  }
                />
                <CardContent className={classes.cardContent}>
                  <div className={classes.numbers}>
                    {draftWidgetsOnLayout.map((_, i) => (
                      <Typography
                        className={classes.number}
                        variant="subtitle1"
                        key={`draft-widget-${i}`}
                      >
                        {i + 1}
                      </Typography>
                    ))}
                  </div>
                  <SortableList
                    // @ts-ignore: incorrect typing interpretation
                    items={draftWidgetsOnLayout}
                    onSortEnd={handleSortEnd}
                    onLayoutChange={handleSortEnd}
                    useDragHandle
                  />
                </CardContent>
              </Card>
            </div>
            <PageFormToolbar dirty={dirty}>
              <CancelButton onClick={resetForm} />
              <SaveButton label="Save and Next" onClick={() => handleSave()} />
            </PageFormToolbar>
          </Form>
        );
      }}
    </Formik>
  );
}

const useStyles = makeStyles(theme => ({
  root: {},
  form: {
    maxWidth: 'calc(100vw - 300px)',
  },
  dragHandleIcon: {
    marginLeft: theme.spacing(),
    cursor: 'move',
    // @ts-ignore
    color: theme.colors.gray1,
  },
  fieldNameContainer: {
    flex: 1,
  },
  fieldName: {
    marginLeft: theme.spacing(2),
  },
  rightIconsContainer: {
    marginRight: theme.spacing(1),
  },
  removeCircleIconButton: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
  removeCircleIcon: {
    height: 16,
    width: 16,
    // @ts-ignore
    color: theme.colors.gray1,
  },
  cards: {
    display: 'flex',
    gap: `${theme.spacing(3)}px`,
    width: 'calc(100vw - 300px)',
    maxWidth: '90%',
    paddingRight: theme.spacing(2),
  },
  card: {
    flex: 1,
    minWidth: 450,
  },
  cardContent: {
    display: 'flex',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    // @ts-ignore
    backgroundColor: theme.colors.grayBlue,
    marginBottom: theme.spacing(),
  },
  widgetSelector: {
    position: 'relative',
    display: 'inline',
    zIndex: 'auto',
  },
  number: {
    height: 40,
    marginBottom: theme.spacing(),
    display: 'flex',
    alignItems: 'center',
  },
  numbers: {
    marginRight: theme.spacing(1.5),
  },
  sortableList: {
    flex: 1,
  },

  infoIcon: {
    cursor: 'pointer',
  },
  previewButton: {
    marginTop: theme.spacing(),
  },
}));

export default LayoutTab;
